import { UserIcon } from '@heroicons/react/16/solid';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { ProductById } from 'src/shared/types/product';
import { Update } from 'src/shared/types/update';

import { cn, Switch, useDisclosure } from 'src/shared/components/ui';
import { ButtonV2, Tab, TabsV2, Typography } from 'src/shared/components/ui';

import useProductUpdates from '../../hooks/useProductUpdates';
import getUpdateStatus from '../../utils/getUpdateStatus';
import { updateSupplierCloaks } from '../../utils/updateSupplierCloaks';
import EmptyUpdates from './EmptyUpdates';
import ProductActionItemList from './ProductActionItemList';
import ProductUpdateModal from './ProductUpdateModal';
import ProductUpdatesInput from './ProductUpdatesInput';
import ProductUpdatesList from './ProductUpdatesList';

interface ProductDetailUpdatesProps {
  product: ProductById;
  updateId?: string;
}

const ProductDetailUpdatesTab: React.FC<ProductDetailUpdatesProps> = ({
  product,
  updateId
}) => {
  const id = useAuthStore((state) => state.user?.id);
  const { data, isSuccess } = useProductUpdates(id as number, product?.id);
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [isOpenInput, setIsOpenInput] = useState(false);
  const [isResolvedOpen, setIsResolvedOpen] = useState(false);
  const navigate = useNavigate();
  const { id: productId } = useParams<{ id: string }>();
  const hasProcessedUpdateId = useRef(false);

  const updates = useMemo(() => {
    let filtered = data;

    if (selectedSupplier !== 'all') {
      // An update links a set of suppliers now, so it matches when any of its
      // cloaks is the selected one.
      filtered = filtered?.filter((update) =>
        updateSupplierCloaks(update).includes(selectedSupplier)
      );
    }

    if (selectedTopics.size > 0 && product?.update_topics) {
      const topicUpdateIds = new Set<number>();

      selectedTopics.forEach((topic) => {
        const updateIds = product.update_topics?.[topic] || [];

        updateIds.forEach((id) => topicUpdateIds.add(id));
      });

      filtered = filtered?.filter((update) => topicUpdateIds.has(update.id));
    }

    return filtered;
  }, [data, selectedSupplier, selectedTopics, product?.update_topics]);

  // The API returns cloaks in unspecified order (unordered queryset
  // union), so sort here to match the Quotes and Samples tabs.
  const cloaks = useMemo(
    () =>
      [...(product?.cloaks || [])].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      ),
    [product?.cloaks]
  );

  const renderSuppliers = () => {
    return cloaks.map((cloak) => (
      <Tab
        key={cloak}
        title={
          <div className="flex items-center gap-1">
            <UserIcon className="size-4" />
            <span>{cloak}</span>
          </div>
        }
      />
    ));
  };

  const actionItems = useMemo(() => {
    if (updates) {
      return updates?.filter((update) => {
        const type = getUpdateStatus(update);

        return type === 'action';
      });
    }

    return [];
  }, [updates]);

  const notActionsItems = useMemo(() => {
    if (updates) {
      return updates?.filter((update) => {
        const type = getUpdateStatus(update);

        return type !== 'action';
      });
    }

    return [];
  }, [updates]);

  const relevantUpdates = useMemo(() => {
    return notActionsItems?.filter((update) => update?.relevant);
  }, [notActionsItems]);

  const relevantUpdatesTotal = useMemo(() => {
    if (relevantUpdates) {
      return relevantUpdates.length;
    }

    return 0;
  }, [relevantUpdates]);

  const [selectedUpdate, setSelectedUpdate] = useState<null | Update>(null);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const hasOpenedModal = useRef(false);

  useEffect(() => {
    if (selectedUpdate && isSuccess) {
      const updatedSelectedUpdate = updates?.find(
        (update) => update.id === selectedUpdate.id
      );

      if (updatedSelectedUpdate) {
        setSelectedUpdate(updatedSelectedUpdate);
      }
    }
  }, [updates, selectedUpdate, isSuccess]);

  useEffect(() => {
    if (!isOpen && hasOpenedModal.current) {
      setSelectedUpdate(null);
      if (updateId) {
        navigate(`${ROUTE_PATHS.PRODUCTS}/${productId}?tab=updates`, {
          replace: true
        });
      }
    }
  }, [isOpen, updateId, navigate, productId]);

  useEffect(() => {
    hasProcessedUpdateId.current = false;
    hasOpenedModal.current = false;
  }, [updateId]);

  useEffect(() => {
    if (updateId && data && isSuccess && !hasProcessedUpdateId.current) {
      const updateIdNumber = Number(updateId);
      const foundUpdate = data.find((update) => update.id === updateIdNumber);

      if (foundUpdate) {
        if (!foundUpdate.relevant) {
          setIsResolvedOpen(true);
        }

        setSelectedUpdate(foundUpdate);
        onOpen();
        hasOpenedModal.current = true;
        hasProcessedUpdateId.current = true;
      }
    }
  }, [updateId, data, isSuccess, onOpen]);

  const topics = useMemo(() => {
    if (!product?.update_topics) {
      return [];
    }

    return Object.keys(product.update_topics);
  }, [product?.update_topics]);

  const toggleTopic = (topic: string) => {
    setSelectedTopics((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(topic)) {
        newSet.delete(topic);
      } else {
        newSet.add(topic);
      }

      return newSet;
    });
  };

  return (
    <div className="flex flex-col flex-grow gap-4 h-full">
      <div className="flex items-center justify-between h-[33px]">
        <TabsV2
          selectedKey={selectedSupplier}
          onSelectionChange={(key) => setSelectedSupplier(key as string)}
          color="dark"
          variant="light"
          classNames={{ tabList: '!px-0' }}
        >
          <Tab key="all" title="All" />
          {renderSuppliers()}
        </TabsV2>

        <Switch
          onValueChange={(isSelected) => setIsResolvedOpen(isSelected)}
          isSelected={isResolvedOpen}
          size="sm"
          color="primary"
          className="flex-row-reverse"
          classNames={{
            label: '!flex-row-reverse mr-2'
          }}
        >
          <Typography variant="body-1" weight="bold" className="text-primary">
            Include resolved updates
          </Typography>
        </Switch>
      </div>

      {topics.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {topics.map((topic) => (
            <ButtonV2
              key={topic}
              onPress={() => toggleTopic(topic)}
              color="tertiary"
              size="sm"
              className={cn(
                'font-semibold',
                selectedTopics.has(topic)
                  ? 'border-black text-black'
                  : 'text-secondary-text'
              )}
            >
              {topic}
            </ButtonV2>
          ))}
          {selectedTopics.size > 0 && (
            <ButtonV2
              color="fourth"
              size="sm"
              className="font-semibold"
              onPress={() => setSelectedTopics(new Set())}
              startContent={<XMarkIcon className="size-4" />}
            >
              Clear
            </ButtonV2>
          )}
        </div>
      )}

      {actionItems?.length > 0 && (
        <div className="flex flex-col gap-3">
          <Typography variant="h4" as="h2" weight="bold">
            {actionItems?.length} Task
            {actionItems?.length === 1 ? '' : 's'}
          </Typography>

          <ProductActionItemList updates={actionItems} />
        </div>
      )}

      <div className="flex justify-between items-center gap-4">
        <Typography variant="h4" as="h2" weight="bold">
          {isSuccess && (
            <>
              {isResolvedOpen ? notActionsItems?.length : relevantUpdatesTotal}{' '}
              {isResolvedOpen
                ? notActionsItems?.length === 1
                  ? 'Update'
                  : 'Updates'
                : relevantUpdatesTotal === 1
                  ? 'Update'
                  : 'Updates'}
            </>
          )}
        </Typography>

        {!isOpenInput && (
          <ButtonV2 onPress={() => setIsOpenInput(!isOpenInput)} size="md">
            Message us
          </ButtonV2>
        )}
      </div>

      {isOpenInput && (
        <ProductUpdatesInput
          key={product?.id}
          productId={product.id}
          suppliers={cloaks}
          onClose={() => setIsOpenInput(false)}
        />
      )}

      {notActionsItems && notActionsItems?.length > 0 && isResolvedOpen && (
        <ProductUpdatesList updates={notActionsItems as any[]} />
      )}

      {relevantUpdatesTotal > 0 && !isResolvedOpen && (
        <ProductUpdatesList updates={relevantUpdates as any[]} />
      )}

      {notActionsItems?.length === 0 && isResolvedOpen && isSuccess ? (
        <EmptyUpdates
          title="No updates"
          description="There are no updates for this product"
        />
      ) : null}

      {relevantUpdatesTotal === 0 && !isResolvedOpen && isSuccess ? (
        <EmptyUpdates
          title="No unresolved updates"
          description="There are no unresolved updates for this product"
        />
      ) : null}

      <ProductUpdateModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        selectedUpdate={selectedUpdate as Update}
        updates={updates as Update[]}
        setSelectedUpdate={setSelectedUpdate}
      />
    </div>
  );
};

export default ProductDetailUpdatesTab;
